import { calculateOffset } from 'src/app/common/app.helpers';
import FieldsConstants from 'src/app/common/fields.constants';
import {
  FormSearchParams,
  SearchAttributes,
  SearchRequestObject,
  SearchResultResponse,
  SearchState,
  SortingOrderType,
  SortParams,
} from 'src/app/interfaces/search.interface';
import { Operator, SearchResponse } from 'src/app/swagger-api';
import { SessionService } from '../../auth/session.service';
import { CommonMapSearchValues } from '../../common/common-map-search.values';
import { MapSearchRequestCreatorFactory } from '../map/markers-handler-helpers/factories/map-search-request-creator.factory';
import { SearchCategories, SearchQueryMode } from './search.enum';

export interface SearchStrategy {
  sortParams?: SortParams;
  searchObject?: SearchRequestObject;

  getProcessedResponse(response: SearchResponse, state: SearchState): SearchResultResponse;

  /**
   * @description Forms query attribute for default or invite users search by passed params.
   * @param { FormSearchParams } args
   * @returns { string }
   */
  formSearchParams(args: FormSearchParams): string;
}

export abstract class SharedDefaultSearch implements SearchStrategy {
  public searchObject: SearchRequestObject;

  public formSearchParams(args: FormSearchParams): string {
    let limit: number = FieldsConstants.SEARCH_DEFAULT_RESULTS_COUNT;

    if (args.currentState.pagination) {
      limit = args.currentState.pagination.limit || limit;
    }

    const offset: number = this.calculateOffset(args.previousState, args.currentState, limit);

    let isAscending = false;
    let sortName: string = CommonMapSearchValues.SORT_ATTRIBUTE_SCORE;

    if (args.currentState.sortParams) {
      isAscending = args.currentState.sortParams.ascending;
      switch (args.currentState.sortParams.orderType) {
        case SortingOrderType.NAME:
          sortName = CommonMapSearchValues.SORT_ATTRIBUTE_DISPLAY_NAME;
          break;
        case SortingOrderType.DATE:
          sortName = CommonMapSearchValues.SORT_ATTRIBUTE_CREATED;
          break;
        case SortingOrderType.SCORE:
          sortName = CommonMapSearchValues.SORT_ATTRIBUTE_SCORE;
          break;
      }
    }

    this.searchObject = {
      search_attributes: [],
      sort_attributes: [
        {
          name: sortName,
          ascending: isAscending,
        },
      ],
    };

    this.setSearchQueryAttr(args.currentState, this.searchObject);
    this.setSearchItemsAttr(args.currentState, this.searchObject);

    this.searchObject.search_attributes = this.applyAdditionalAttrs(
      this.searchObject.search_attributes,
      args.currentState,
    );

    return JSON.stringify(offset > -1 ? Object.assign(this.searchObject, { limit, offset }) : this.searchObject);
  }

  public getProcessedResponse(response: SearchResponse, state: SearchState): SearchResultResponse {
    return {
      search_results: response.search_results,
      total_matches_count: response.total_matches_count,
      params: state.params,
      pagination: state.pagination,
    };
  }

  protected abstract applyAdditionalAttrs(attrs: SearchAttributes[], state?: SearchState): SearchAttributes[];

  private calculateOffset(previousState: SearchState, currentState: SearchState, limit: number): number {
    return calculateOffset(previousState, currentState, limit);
  }

  private setSearchQueryAttr(currentState: SearchState, searchObject: SearchRequestObject): void {
    if (currentState.params?.queryText) {
      searchObject.search_attributes.push({
        name: CommonMapSearchValues.SEARCH_ATTRIBUTE_SEARCH_QUERY,
        operator: Operator.Eq,
        values: [currentState.params.queryText],
      });
    }
  }

  private setSearchItemsAttr(currentState: SearchState, searchObject: SearchRequestObject): void {
    if (currentState.params?.itemKind === SearchCategories.ALL_CATEGORIES) {
      searchObject.search_attributes.push({
        name: CommonMapSearchValues.SEARCH_ATTRIBUTE_SEARCH_ITEMS,
        operator: Operator.Eq,
        values: [
          SearchCategories.COMMUNITIES,
          SearchCategories.PROJECTS,
          SearchCategories.POSTS,
          SearchCategories.USERS,
          SearchCategories.COMMENTS,
        ],
      });
    } else {
      searchObject.search_attributes.push({
        name: CommonMapSearchValues.SEARCH_ATTRIBUTE_SEARCH_ITEMS,
        operator: Operator.Eq,
        values: [currentState.params.itemKind],
      });
    }
  }
}

export class DefaultSearch extends SharedDefaultSearch {
  protected applyAdditionalAttrs(attrs: SearchAttributes[]): SearchAttributes[] {
    return attrs;
  }
}

export class InviteUsersSearch extends SharedDefaultSearch {
  public constructor(protected sessionService: SessionService) {
    super();
  }

  protected applyAdditionalAttrs(attrs: SearchAttributes[], state: SearchState): SearchAttributes[] {
    return attrs.concat([
      {
        name: CommonMapSearchValues.SEARCH_ATTRIBUTE_SEARCH_EXCLUSION,
        operator: Operator.Eq,
        values: [this.sessionService.userId].concat(state.excludeIds ?? []),
      },
    ]);
  }
}

export class InviteByNamesSearch extends SharedDefaultSearch {
  public constructor(protected sessionService: SessionService) {
    super();
  }

  protected applyAdditionalAttrs(attrs: SearchAttributes[], _state: SearchState): SearchAttributes[] {
    // Users are invited only by names, don't do a full-text search by all fields
    attrs.push({
      name: CommonMapSearchValues.SEARCH_ATTRIBUTE_QUERY_MODE,
      operator: Operator.Eq,
      values: [SearchQueryMode.NAMES],
    });
    return attrs.concat([
      {
        name: CommonMapSearchValues.SEARCH_ATTRIBUTE_SEARCH_EXCLUSION,
        operator: Operator.Eq,
        values: [this.sessionService.userId],
      },
    ]);
  }
}

export class MapSearch implements SearchStrategy {
  public getProcessedResponse(response: SearchResponse, state: SearchState): SearchResultResponse {
    return {
      search_results: response.search_results,
      total_matches_count: response.total_matches_count,
      params: state.data,
    };
  }

  public formSearchParams(args: FormSearchParams): string {
    const request: SearchRequestObject = MapSearchRequestCreatorFactory.createMapSearchRequest(
      args.currentState.data.currentState,
      args.applyLocation,
      args.locationLiteral,
    );

    return JSON.stringify({ ...request, limit: 100 });
  }
}

export class EntityNameSearch extends SharedDefaultSearch {
  protected applyAdditionalAttrs(attrs: SearchAttributes[]): SearchAttributes[] {
    return [
      ...attrs,
      ...[
        {
          name: CommonMapSearchValues.SEARCH_ATTRIBUTE_QUERY_MODE,
          operator: Operator.Eq,
          values: [SearchQueryMode.NAMES],
        },
      ],
    ];
  }
}
